import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { AlertChannel, NOTIFICATION_TOPICS } from '@core/models/shopper.enum';
import { ShopperAddressState, ShopperPaymentMethodState, ShopperState } from '@core/models/shopper.state';
import { MockDataService } from '@core/services/mock-data.service';
import { LOCATION_PRESETS } from '@core/services/shopper-location.service';

import { mockError, mockResponse } from '../mock-response';
import {
  OAuthProvider,
  SaveShopperAddressRequest,
  SaveShopperPaymentMethodRequest,
  ShopperRegisterRequest,
  ShopperRepository,
  UpdateShopperNotificationsRequest,
  UpdateShopperProfileRequest
} from './shopper.repository';
import { seedShoppers } from './shopper.seed';

const COLLECTION = 'shoppers';

/**
 * Mock ZIP resolution: exact preset match wins, otherwise numeric ZIP
 * closeness picks the nearest Local Finds preset (a fair geographic proxy for
 * US ZIP prefixes), falling back to the LA default. The real API geocodes.
 */
function presetLocationForZip(zip: string): ShopperState['defaultLocation'] {
  const exact = LOCATION_PRESETS.find(preset => preset.zip === zip);
  if (exact) return { ...exact };
  const target = Number(zip);
  if (!Number.isFinite(target)) return { ...LOCATION_PRESETS[0] };
  const nearest = [...LOCATION_PRESETS].sort(
    (a, b) => Math.abs(Number(a.zip) - target) - Math.abs(Number(b.zip) - target)
  )[0];
  return { ...nearest };
}

/** Default-first ordering keeps positional readers (checkout preselect) correct. */
function withDefaultFirst(addresses: ShopperAddressState[]): ShopperAddressState[] {
  const defaultAddress = addresses.find(address => address.isDefault);
  if (!defaultAddress) return addresses;
  return [defaultAddress, ...addresses.filter(address => address !== defaultAddress)];
}

/** The same rule for cards - checkout preselects the first one it is handed. */
function defaultMethodFirst(methods: ShopperPaymentMethodState[]): ShopperPaymentMethodState[] {
  const fallback = methods.find(method => method.isDefault);
  if (!fallback) return methods;
  return [fallback, ...methods.filter(method => method !== fallback)];
}

@Injectable()
export class ShopperMockRepository implements ShopperRepository {
  private readonly shoppers = inject(MockDataService).collection<ShopperState>(COLLECTION, seedShoppers);

  /** Mock auth: the seeded account signs in with any non-empty password. */
  signIn(email: string, password: string): Observable<ShopperState> {
    const shopper = this.findByEmail(email);
    if (!shopper || !password) {
      return mockError('We couldn’t sign you in with that email and password.');
    }
    return mockResponse(shopper);
  }

  /** Mock registration - the password is accepted as-is, never stored. */
  register(request: ShopperRegisterRequest): Observable<ShopperState> {
    if (this.findByEmail(request.email)) {
      return mockError('An account with this email already exists.');
    }
    const shopper: ShopperState = this.shoppers.upsert({
      id: this.shoppers.allocateId(),
      displayName: `${request.firstName.trim()} ${request.lastName.trim()}`,
      firstName: request.firstName.trim(),
      email: request.email.trim().toLowerCase(),
      phone: null,
      defaultLocation: presetLocationForZip(request.zip.trim()),
      addresses: [],
      notificationChannel: AlertChannel.Email,
      notificationTopics: [...NOTIFICATION_TOPICS],
      paymentMethods: []
    });
    return mockResponse(this.clone(shopper));
  }

  getById(id: number): Observable<ShopperState | null> {
    return mockResponse(this.shoppers.get(id) ?? null);
  }

  updateProfile(shopperId: number, request: UpdateShopperProfileRequest): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    const updated = this.shoppers.patch(shopperId, {
      displayName: request.displayName.trim(),
      firstName: request.firstName.trim(),
      phone: request.phone?.trim() || null,
      defaultLocation: { ...request.defaultLocation }
    })!;
    return mockResponse(this.clone(updated));
  }

  /** Mock password change - nothing is stored, so only presence is checked. */
  changePassword(shopperId: number, currentPassword: string, newPassword: string): Observable<void> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    if (!currentPassword) return mockError('Enter your current password.');
    if (newPassword.length < 8) return mockError('Your new password needs at least 8 characters.');
    return mockResponse(undefined);
  }

  saveAddress(shopperId: number, request: SaveShopperAddressRequest): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    let addresses = shopper.addresses.map(address => ({ ...address }));
    let savedId: number;
    if (request.id === null) {
      savedId = Math.max(0, ...addresses.map(address => address.id)) + 1;
      // The first saved address is always the default.
      addresses.push({ ...request, id: savedId, isDefault: request.isDefault || addresses.length === 0 });
    } else {
      const index = addresses.findIndex(address => address.id === request.id);
      if (index < 0) return mockError('That address no longer exists.');
      savedId = request.id;
      addresses[index] = { ...request, id: request.id, isDefault: request.isDefault || addresses[index].isDefault };
    }
    if (request.isDefault) {
      addresses = addresses.map(address => ({ ...address, isDefault: address.id === savedId }));
    }
    const updated = this.shoppers.patch(shopperId, { addresses: withDefaultFirst(addresses) })!;
    return mockResponse(this.clone(updated));
  }

  deleteAddress(shopperId: number, addressId: number): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    const addresses = shopper.addresses.filter(address => address.id !== addressId).map(address => ({ ...address }));
    // Deleting the default promotes the next address so one default remains.
    if (addresses.length > 0 && !addresses.some(address => address.isDefault)) {
      addresses[0] = { ...addresses[0], isDefault: true };
    }
    const updated = this.shoppers.patch(shopperId, { addresses })!;
    return mockResponse(this.clone(updated));
  }

  setDefaultAddress(shopperId: number, addressId: number): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    if (!shopper.addresses.some(address => address.id === addressId)) {
      return mockError('That address no longer exists.');
    }
    const addresses = shopper.addresses.map(address => ({ ...address, isDefault: address.id === addressId }));
    const updated = this.shoppers.patch(shopperId, { addresses: withDefaultFirst(addresses) })!;
    return mockResponse(this.clone(updated));
  }

  updateNotifications(shopperId: number, request: UpdateShopperNotificationsRequest): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    const updated = this.shoppers.patch(shopperId, {
      notificationChannel: request.notificationChannel,
      notificationTopics: [...request.notificationTopics]
    })!;
    return mockResponse(this.clone(updated));
  }

  private findByEmail(email: string): ShopperState | undefined {
    const normalized = email.trim().toLowerCase();
    return this.shoppers.list().find(shopper => shopper.email.toLowerCase() === normalized);
  }

  /** Detach the returned snapshot from the in-memory collection. */
  private clone(shopper: ShopperState): ShopperState {
    return {
      ...shopper,
      defaultLocation: { ...shopper.defaultLocation },
      addresses: shopper.addresses.map(address => ({ ...address })),
      notificationTopics: [...shopper.notificationTopics]
    };
  }

  savePaymentMethod(shopperId: number, request: SaveShopperPaymentMethodRequest): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');

    const nextId = Math.max(0, ...shopper.paymentMethods.map(method => method.id)) + 1;
    const saved: ShopperPaymentMethodState = { ...request, id: nextId };
    // First card on the account is the default whatever the request said.
    const isDefault = request.isDefault || shopper.paymentMethods.length === 0;
    const methods = [
      ...shopper.paymentMethods.map(m => ({ ...m, isDefault: isDefault ? false : m.isDefault })),
      { ...saved, isDefault }
    ];
    const updated = this.shoppers.patch(shopperId, { paymentMethods: defaultMethodFirst(methods) })!;
    return mockResponse(this.clone(updated));
  }

  deletePaymentMethod(shopperId: number, paymentMethodId: number): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');

    const remaining = shopper.paymentMethods.filter(method => method.id !== paymentMethodId);
    if (remaining.length === shopper.paymentMethods.length) {
      return mockError('That payment method no longer exists.');
    }
    // Removing the default promotes the next card so checkout always has one.
    if (remaining.length > 0 && !remaining.some(method => method.isDefault)) {
      remaining[0] = { ...remaining[0], isDefault: true };
    }
    const updated = this.shoppers.patch(shopperId, { paymentMethods: defaultMethodFirst(remaining) })!;
    return mockResponse(this.clone(updated));
  }

  setDefaultPaymentMethod(shopperId: number, paymentMethodId: number): Observable<ShopperState> {
    const shopper = this.shoppers.get(shopperId);
    if (!shopper) return mockError('We couldn’t find your account.');
    if (!shopper.paymentMethods.some(method => method.id === paymentMethodId)) {
      return mockError('That payment method no longer exists.');
    }
    const methods = shopper.paymentMethods.map(method => ({ ...method, isDefault: method.id === paymentMethodId }));
    const updated = this.shoppers.patch(shopperId, { paymentMethods: defaultMethodFirst(methods) })!;
    return mockResponse(this.clone(updated));
  }

  /**
   * OAuth sign-in (ACC-01). The provider handshake is out of scope for the
   * mock, so a known email signs in and an unknown one is rejected the same
   * way a real callback would when no account is linked.
   */
  signInWithProvider(provider: OAuthProvider, email: string): Observable<ShopperState> {
    const shopper = this.shoppers.list().find(s => s.email.toLowerCase() === email.trim().toLowerCase());
    if (!shopper) {
      return mockError(
        `No Brijj Finds account is linked to that ${provider === 'google' ? 'Google' : 'Apple'} address.`
      );
    }
    return mockResponse(this.clone(shopper));
  }
}
